import { z } from 'zod';
import type { AnalysisConfig } from './analysis';

/**
 * Values of Forma fields that a project uses (request 20 of round 4). A list field of Forma (e.g. "Geschoss" with 15
 * floors, "Gewerk", "Leistungsphase") holds the standard values of the whole Forma account; a building with 4 floors
 * uses 4 of them, a project perhaps one trade, another a work stage with 2 or 3 trades. The project keeps, per field,
 * the values it uses: for the whole project, per location of the Forma location tree (location, sub-location) and, when
 * the analysis takes the buildings from a field (e.g. "Teilprojekt"), per building.
 *
 * Projektzeit never writes to Forma: the scope only says which values the analysis uses. A task whose value lies
 * outside the scope keeps its value in the task lists; in the analysis the value counts as "no value" (no floor is drawn
 * that the building does not have) and the analysis lists these tasks, so the value can be corrected in Forma or the
 * scope extended. Floors of the scope without a task are drawn as empty floors, so the building shows all its floors.
 */

export interface FieldScope {
  /** values used in the whole project; missing or empty = every value */
  values?: string[];
  /** values per location node (Forma location id); the scope of the nearest location of a task applies */
  byLocation?: Record<string, string[]>;
  /** values per building, by the building of the analysis (value of the building field) */
  byBuilding?: Record<string, string[]>;
}

/** field id (Forma attribute id) -> scope */
export type AttributeScope = Record<string, FieldScope>;

const value = z.string().trim().min(1).max(200);
const valueList = z.array(value).max(500);

export const attributeScopeSchema = z
  .record(
    z.string().min(1).max(200),
    z.object({
      values: valueList.optional(),
      byLocation: z.record(z.string().min(1).max(200), valueList).optional(),
      byBuilding: z.record(z.string().min(1).max(200), valueList).optional(),
    }),
  )
  .refine((s) => Object.keys(s).length <= 100, 'too many fields')
  .refine((s) => Object.values(s).every((f) => Object.keys(f.byLocation ?? {}).length <= 500 && Object.keys(f.byBuilding ?? {}).length <= 500), 'too many locations');

/** A location of the Forma tree as the scope needs it. */
export interface ScopeLocation {
  id: string;
  parentId: string | null;
  name: string;
}

/** Ids from a node up to the top of the tree (the node first). */
export function locationChain(nodeId: string | null | undefined, byId: Map<string, ScopeLocation>): string[] {
  const out: string[] = [];
  let cur = nodeId ? byId.get(nodeId) : undefined;
  let guard = 0;
  while (cur && guard++ < 50) {
    out.push(cur.id);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return out;
}

/** Where a task is: its location (node first, then the nodes above) and its building in the analysis. */
export interface ScopePlace {
  chain: string[];
  building?: string | null;
}

/**
 * Values a task may have in a field: the scope of the nearest location in the chain (task node first), then the scope
 * of its building, otherwise the values of the whole project; null = no restriction. `placeOnly` leaves the project
 * values out (used for the empty floors of a building, where the values of all buildings together would be wrong).
 */
export function allowedValues(scope: AttributeScope | null | undefined, fieldId: string, place: ScopePlace, placeOnly = false): string[] | null {
  const f = scope?.[fieldId];
  if (!f) return null;
  for (const id of place.chain) {
    const list = f.byLocation?.[id];
    if (list && list.length) return list;
  }
  const b = place.building ? f.byBuilding?.[place.building] : undefined;
  if (b && b.length) return b;
  if (placeOnly) return null;
  return f.values && f.values.length ? f.values : null;
}

export interface OutOfScopeValue {
  field: string;
  value: string;
}

/**
 * The attributes of a task as the analysis uses them: values outside the scope are taken out (they count as "no value")
 * and returned in `outOfScope`, so they can be listed. Without a scope the attributes stay as they are. `buildingField`:
 * the field the analysis takes the buildings from (its value selects the scope per building).
 */
export function scopeAttributes(
  attributes: Record<string, string>,
  scope: AttributeScope | null | undefined,
  chain: string[],
  buildingField?: string | null,
): { attributes: Record<string, string>; outOfScope: OutOfScopeValue[] } {
  if (!scope || !Object.keys(scope).length) return { attributes, outOfScope: [] };
  // the building of the task, unless its own value lies outside the scope (then no building scope applies)
  let building: string | null = buildingField ? attributes[buildingField] || null : null;
  if (building && buildingField) {
    const allowed = allowedValues(scope, buildingField, { chain });
    if (allowed && !allowed.includes(building)) building = null;
  }
  const out: OutOfScopeValue[] = [];
  let next: Record<string, string> | null = null;
  for (const [field, v] of Object.entries(attributes)) {
    if (!v) continue;
    const allowed = allowedValues(scope, field, field === buildingField ? { chain } : { chain, building });
    if (!allowed || allowed.includes(v)) continue;
    out.push({ field, value: v });
    next ??= { ...attributes };
    delete next[field];
  }
  return { attributes: next ?? attributes, outOfScope: out };
}

/**
 * A scope as stored: only fields of the project, only values the field offers (list fields; free fields keep any value),
 * only locations and buildings of the project, no duplicates, no empty lists; a list with every value of a field is no
 * restriction.
 */
export function cleanScope(raw: AttributeScope, fields: { id: string; options: string[] }[], locationIds: Set<string>, buildings: Set<string> = new Set()): AttributeScope {
  const out: AttributeScope = {};
  const byField = new Map(fields.map((f) => [f.id, f]));
  const pick = (list: string[] | undefined, options: string[]) => {
    const offered = new Set(options);
    const seen = new Set<string>();
    const keep: string[] = [];
    for (const v of list ?? []) {
      const s = v.trim();
      if (!s || seen.has(s) || (options.length && !offered.has(s))) continue;
      seen.add(s);
      keep.push(s);
    }
    // in the order of the field (the order of Forma), free values after
    keep.sort((a, b) => (offered.has(a) && offered.has(b) ? options.indexOf(a) - options.indexOf(b) : 0));
    return keep;
  };
  for (const [id, f] of Object.entries(raw)) {
    const def = byField.get(id);
    if (!def) continue;
    const values = pick(f.values, def.options);
    const all = def.options.length > 0 && values.length === def.options.length;
    const byLocation: Record<string, string[]> = {};
    for (const [loc, list] of Object.entries(f.byLocation ?? {})) {
      if (!locationIds.has(loc)) continue;
      const v = pick(list, def.options);
      if (v.length) byLocation[loc] = v;
    }
    const byBuilding: Record<string, string[]> = {};
    for (const [b, list] of Object.entries(f.byBuilding ?? {})) {
      if (!buildings.has(b)) continue;
      const v = pick(list, def.options);
      if (v.length) byBuilding[b] = v;
    }
    const entry: FieldScope = {};
    if (values.length && !all) entry.values = values;
    if (Object.keys(byLocation).length) entry.byLocation = byLocation;
    if (Object.keys(byBuilding).length) entry.byBuilding = byBuilding;
    if (entry.values || entry.byLocation || entry.byBuilding) out[id] = entry;
  }
  return out;
}

/**
 * Floors of each building that the scope defines (for empty floors in the building picture), by the label the analysis
 * uses for the building: buildings from a field: the scope per building; buildings from the location tree: the scope of
 * the building node (or a node above it), else per building; without buildings: the values of the whole project.
 * Floors from the location tree: nothing (the tree itself holds the floors).
 */
export function scopedBuildingFloors(
  scope: AttributeScope | null | undefined,
  config: AnalysisConfig,
  locations: ScopeLocation[],
  projectLabel: string,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  if (!scope || config.floor.kind !== 'attr') return out;
  const fieldId = config.floor.id;
  if (config.building.kind === 'none') {
    const v = allowedValues(scope, fieldId, { chain: [] });
    if (v) out[projectLabel] = v;
    return out;
  }
  if (config.building.kind === 'attr') {
    for (const [b, list] of Object.entries(scope[fieldId]?.byBuilding ?? {})) if (list.length) out[b] = [...list];
    return out;
  }
  const level = config.building.level;
  const byId = new Map(locations.map((l) => [l.id, l]));
  for (const l of locations) {
    const chain = locationChain(l.id, byId);
    // the chain runs from the node to the top: a node of the building level has exactly `level` nodes in it
    if (chain.length !== level) continue;
    const v = allowedValues(scope, fieldId, { chain, building: l.name }, true);
    if (!v) continue;
    const list = (out[l.name] ??= []);
    for (const x of v) if (!list.includes(x)) list.push(x);
  }
  return out;
}

/** Tasks with values outside the scope, per field and value (for the notice in the analysis). */
export function outOfScopeSummary(tasks: { outOfScope?: OutOfScopeValue[] }[]): { field: string; value: string; tasks: number }[] {
  const m = new Map<string, { field: string; value: string; tasks: number }>();
  for (const t of tasks) {
    for (const o of t.outOfScope ?? []) {
      const k = `${o.field}\u0000${o.value}`;
      const row = m.get(k) ?? { field: o.field, value: o.value, tasks: 0 };
      row.tasks++;
      m.set(k, row);
    }
  }
  return [...m.values()].sort((a, b) => b.tasks - a.tasks || a.value.localeCompare(b.value, 'de', { numeric: true }));
}
